import { FiltersDefinition, FiltersState, IconType } from '@factorialco/f0-react'
import { DateRange, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import { ReportSchemaType } from 'modules/ai_reports/lib/types'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'

import { getRendererDef } from './tableRendererRegistry'
import { DatasourceType } from './types'
import { ColumnFormatHint, datasourceTypeFromId } from './utils'

interface ReportTableAction {
  label: string
  icon: IconType
  onClick: () => void
}

type Props = {
  id: string
  dateFilter?: DateRange
  reportName: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ColumnFormatHint[]
  onStateChange: React.ComponentProps<typeof OneDataCollection>['onStateChange']
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters?: FiltersState<FiltersDefinition<string>>
  schema: ReportSchemaType
}

/**
 * Helper function to derive filters from dateFilter and datasource type.
 * This converts the dateFilter prop into the appropriate filter format based on
 * the datasource type (snapshot, traceability, or regular report).
 */
export const deriveFiltersFromDateFilter = (
  dateFilter: DateRange | undefined,
  sourceId: string
): AiReportsResult['filters'] => {
  const datasourceType = datasourceTypeFromId(sourceId)

  if (!dateFilter) return {}

  // For snapshot tables, use the 'from' date as snapshot date
  if (datasourceType === 'snapshot') {
    return {
      snapshotDate: dateFilter.from ? dateFilter.from.toISOString() : undefined,
    }
  }

  // For traceability tables, use the full date range
  if (datasourceType === 'traceability') {
    if (dateFilter.from && dateFilter.to) {
      return {
        dateRange: {
          from: moment(dateFilter.from).startOf('day').toISOString(),
          to: moment(dateFilter.to).endOf('day').toISOString(),
        },
      }
    }
  }

  // For regular reports, no date filters needed
  return {}
}

const pickRenderer = (datasourceType: DatasourceType) => {
  const def = getRendererDef(datasourceType)

  return def.renderer
}

const DatasourceTypeRenderer = ({
  id,
  reportName,
  sqlQuery,
  tableId,
  actions,
  columnFormatHints,
  onStateChange,
  dateFilter,
  onUserFacingSqlQueryChange,
  currentFilters,
  schema,
}: Props) => {
  const datasourceType = datasourceTypeFromId(tableId)
  const Renderer = pickRenderer(datasourceType)

  return (
    <Renderer
      id={id}
      dateFilter={dateFilter}
      tableId={tableId}
      reportName={reportName}
      sqlQuery={sqlQuery}
      actions={actions}
      columnFormatHints={columnFormatHints}
      onStateChange={onStateChange}
      onUserFacingSqlQueryChange={onUserFacingSqlQueryChange}
      currentFilters={currentFilters}
      schema={schema}
    />
  )
}

export default DatasourceTypeRenderer
